import {
  CheckInCircleIcon,
  CrossInCircleIcon,
  ExclamationIcon,
  DataTable as Table,
  FilterControls,
  formatDate,
  formatMoney,
  useFilter,
  ReloadIcon,
} from '@setel/portal-ui';
import * as React from 'react';
import { AxiosError } from 'axios';
import cx from 'classnames';
import { Link, useLocation } from 'react-router-dom';
import { ExternalIcon, UserCircleIcon } from '@setel/web-utils';
import { environments } from 'environments/environment';
import { UserNotFoundPage } from 'modules/accounts/account-details';
import { useGetUserById } from 'modules/accounts/accounts.queries';
import { LoadingPage } from 'modules/pages/loading-page';
import { useIndexStoreOrders } from '../store-order.queries';
import { getStoreOrderBadgeColor } from '../store-order.helper';
import { pageview } from 'shared/analytics/gtag';

type StoreOrdersListingProps = {
  userId: string;
};

export function StoreOrderListing({ userId }: StoreOrdersListingProps) {
  // added ga for url tracking
  let location = useLocation();
  React.useEffect(() => {
    pageview(location.pathname, 'Store Order');
  }, [location]);

  const { data, isError, isLoading } = useGetUserById(userId, {
    retry: (retryCount, err) => {
      const error = err as AxiosError;
      return retryCount < 3 && error?.response?.status !== 404;
    },
  });

  return (
    <>
      {isLoading && !data ? (
        <LoadingPage />
      ) : isError ? (
        <UserNotFoundPage />
      ) : (
        <React.Fragment>
          <div className="flex justify-between items-center px-4 py-3 border-gray-200 border border-b-0 bg-[#f5f5f5]">
            <div className="inline-flex h-10 items-center">
              <UserCircleIcon className="text-3xl text-gray-500 mr-3" />
              <div className="flex flex-col">
                <h1 className="text-sm">{data?.fullName}</h1>
                <h2 className="text-sm text-lightgrey">
                  {data?.phone.replace('6', '')}
                </h2>
              </div>
            </div>
          </div>

          <StoreOrdersTable userId={userId} />
        </React.Fragment>
      )}
    </>
  );
}

type StoreOrdersTableProps = {
  userId: string;
};

export function StoreOrdersTable({ userId }: StoreOrdersTableProps) {
  const filter = useFilter<{ createdAt: [string, string] }>(
    { createdAt: ['', ''] },
    {
      components: [
        {
          key: 'createdAt',
          type: 'daterange',
          props: {
            label: 'Created on',
          },
        },
      ],
    }
  );

  const {
    data: storeOrders,
    isError,
    isLoading,
    refetch,
  } = useIndexStoreOrders(
    { perPage: 100, page: 1 },
    {
      userId,
      from: filter[0].values.createdAt?.[0],
      to: filter[0].values.createdAt?.[1],
    },
    {
      retry: (retryCount, err) => {
        const error = err as AxiosError;
        return retryCount < 3 && error?.response?.status !== 404;
      },
    }
  );

  return (
    <div className="border-gray-200 border">
      <FilterControls className="shadow-none pb-4" filter={filter} />
      <div className={'mb-4 text-center flex justify-center'}>
        <p
          className="text-brand-500 tracking-1 font-bold pu-cursor-pointer leading-4"
          onClick={() => refetch()}
        >
          <span className="text-xs">REFRESH LIST</span>
          <ReloadIcon
            className="ml-2 inline"
            style={{ width: 16, height: 16 }}
          />
        </p>
      </div>
      <div
        className="overflow-y-auto relative max-h-[283px]"
        data-react-setel-scrollbar
      >
        <Table
          type="secondary"
          isLoading={isLoading}
          data-testid="store-orders-table"
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th className="sticky top-0 pl-[39.1px]">Amount</Table.Th>
              <Table.Th className="sticky top-0 text-right">
                Created On
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {!isLoading &&
              storeOrders &&
              storeOrders.items.map((storeOrder) => (
                <Table.Tr
                  key={storeOrder.id}
                  render={(props) => (
                    <Link
                      {...props}
                      to={`/store-orders/${userId}/order/${storeOrder.id}`}
                      data-testid={`store-orders-listing-row-${storeOrder.id}`}
                    ></Link>
                  )}
                >
                  <Table.Td>
                    <StatusIcon
                      color={getStoreOrderBadgeColor(
                        storeOrder.storeOrderStatus
                      )}
                    />
                    {storeOrder?.totalAmount
                      ? `RM${formatMoney(storeOrder.totalAmount)}`
                      : '-'}
                  </Table.Td>
                  <Table.Td className="text-right">
                    {storeOrder?.createdAt
                      ? formatDate(storeOrder.createdAt)
                      : '-'}
                  </Table.Td>
                </Table.Tr>
              ))}
          </Table.Tbody>
          {!isLoading &&
            (!storeOrders || isError || !storeOrders.items.length) && (
              <Table.Caption>
                <div className="py-12" data-testid="store-orders-listing">
                  <p className="text-center text-gray-400 text-sm">
                    No data available.
                  </p>
                </div>
              </Table.Caption>
            )}
        </Table>
      </div>
      <hr />
      <a
        href={`${environments.webAdminUrl}/accounts/${userId}`}
        className="text-brand-500 tracking-1 font-semibold my-4 text-center flex itesm-center justify-center"
        target="_blank"
        rel="noopener noreferrer"
      >
        <span className="text-xs uppercase">View more</span>
        <ExternalIcon className="ml-2 inline" />
      </a>
    </div>
  );
}

type StatusIconProps = {
  color: 'success' | 'lemon' | 'error' | 'grey';
};

const StatusIcon = ({ color }: StatusIconProps) => {
  const iconClasses = 'inline w-3.5 h-3.5 mr-2';
  switch (color) {
    case 'success':
      return <CheckInCircleIcon className={cx(iconClasses, 'text-success')} />;
    case 'lemon':
      return <ExclamationIcon className={cx(iconClasses, 'text-lemon')} />;
    case 'error':
      return <CrossInCircleIcon className={cx(iconClasses, 'text-error')} />;
    default:
      return <span className={iconClasses}></span>;
  }
};
